// SampleRunSection.tsx
import { DetailSection, MetaField, MetaGrid } from '@/components/DetailPage';
import { instrumentRunQueryKeys } from '@/config/queryKeys';
import { useDateFormatter } from '@/hooks';
import { fetchInstrumentRun } from '@/services/instrumentRuns';
import type { InstrumentRunPublic, SampleWithNamesPublic } from '@/types';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

export interface SampleRunSectionProps {
  sample: SampleWithNamesPublic;
  orgId: string;
}

function SampleRunSectionComponent({ sample, orgId }: SampleRunSectionProps) {
  const { t } = useTranslation('studies');
  const formatDate = useDateFormatter();
  const empty = t('empty', { ns: 'general' });

  // Best-effort: the Run section is shown only if the current user can read the run.
  const { data: run } = useQuery<InstrumentRunPublic>({
    queryKey: instrumentRunQueryKeys.detail(orgId, sample.instrument_run_id),
    queryFn: () => fetchInstrumentRun(orgId, sample.instrument_run_id),
    enabled: orgId !== '' && sample.instrument_run_id !== '',
    retry: false,
    staleTime: 5_000,
  });

  if (run === undefined || run === null) return null;

  // lane / library_group are not on SampleWithNamesPublic; read them from the run's
  // sample list when available. TODO(backend): expose lane/library_group on the sample.
  const runSample = run.samples?.find(s => s.id === sample.id);

  return (
    <DetailSection data-testid="sample-run-section" title={t('samples.details.runHeading')}>
      <MetaGrid columns={4}>
        <MetaField
          label={t('samples.details.fieldStarted')}
          placeholder={empty}
          value={run.started_at !== null && run.started_at !== undefined ? formatDate(run.started_at) : undefined}
        />
        <MetaField
          label={t('samples.details.fieldFinished')}
          placeholder={empty}
          value={run.completed_at !== null && run.completed_at !== undefined ? formatDate(run.completed_at) : undefined}
        />
        <MetaField label={t('columnHeaders.lane')} placeholder={empty} value={runSample?.lane ?? undefined} />
        <MetaField label={t('columnHeaders.demuxId')} placeholder={empty} value={sample.demux_index} />
        <MetaField label={t('columnHeaders.well')} placeholder={empty} value={sample.well_position} />
        <MetaField
          label={t('columnHeaders.libraryGroup')}
          placeholder={empty}
          value={runSample?.library_group ?? undefined}
        />
        <MetaField
          label={t('samples.details.fieldName')}
          value={
            <Link params={{ instrumentRunId: sample.instrument_run_id }} to="/instrument_runs/$instrumentRunId">
              {sample.instrument_run_name}
            </Link>
          }
        />
        {/* TODO(route): no instrument detail route yet — render as text. */}
        <MetaField label={t('columnHeaders.instrument')} placeholder={empty} value={run.instrument_name} />
      </MetaGrid>
    </DetailSection>
  );
}

export const SampleRunSection = memo(SampleRunSectionComponent);
